// Browser persistence via localStorage. Lessons are kept in the same JSON
// shape as the Python/EED reference so the data stays portable; progress + UI
// language are stored as small JSON blobs.
//
// The API is intentionally async (Promise-returning) even though localStorage
// is synchronous — it matches the React Native port's storage module, which
// lets store.tsx be shared verbatim and leaves the door open to swapping in
// IndexedDB later without touching any caller.
import { CONTENT_ID, LEGACY_CONTENT_ID } from './types';
import type { Lesson, Progress } from './types';
import type { LangPref } from './i18n';

const K_LESSONS = 's2r.lessons';
const K_PROGRESS = 's2r.progress';
const K_LANG = 's2r.uiLang';

export const DEFAULT_PROGRESS: Progress = {
  show_meaning: true,
  show_word: true,
  repeat_speed: 'normal',
  repeat_count: 'one',
  auto_advance: true,
  last_positions: {},
};

/** localStorage throws in private-mode Safari and when storage is disabled. */
function readKey(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch (e) {
    console.warn(`[storage] read failed for ${key}`, e);
    return null;
  }
}

function writeKey(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch (e) {
    // Most likely QuotaExceededError — surface it rather than failing silently,
    // because the user's sentences would otherwise be lost on reload.
    console.error(`[storage] write failed for ${key}`, e);
    throw e;
  }
}

/**
 * Rewrite the old opaque content id to the current one.
 *
 * Data saved before the rename carries `20120314I0000002` in `content_id` and
 * in every `mp3_path`. Nothing in this app reads those fields — speech is
 * synthesised live — but they travel in exports, so leaving them stale would
 * hand the Android build a path it no longer recognises.
 */
export function migrateContentId(lessons: Lesson[]): { lessons: Lesson[]; changed: boolean } {
  let changed = false;

  const out = lessons.map((lesson) => {
    const staleId = lesson.content_id === LEGACY_CONTENT_ID;
    const staleItems = lesson.items.some((it) => it.mp3_path.includes(LEGACY_CONTENT_ID));
    if (!staleId && !staleItems) return lesson;

    changed = true;
    return {
      ...lesson,
      content_id: staleId ? CONTENT_ID : lesson.content_id,
      items: lesson.items.map((it) =>
        it.mp3_path.includes(LEGACY_CONTENT_ID)
          ? { ...it, mp3_path: it.mp3_path.split(LEGACY_CONTENT_ID).join(CONTENT_ID) }
          : it,
      ),
    };
  });

  return { lessons: changed ? out : lessons, changed };
}

export async function loadLessons(): Promise<Lesson[]> {
  const raw = readKey(K_LESSONS);
  if (!raw) return [];
  try {
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return [];
    const { lessons, changed } = migrateContentId(arr as Lesson[]);
    if (changed) {
      // Persist immediately so the rewrite happens once, not on every load.
      try {
        writeKey(K_LESSONS, JSON.stringify(lessons));
      } catch {
        // A failed write is not fatal — the in-memory data is already migrated.
      }
    }
    return lessons;
  } catch (e) {
    console.warn('[storage] failed to parse lessons', e);
    return [];
  }
}

export async function saveLessons(lessons: Lesson[]): Promise<void> {
  writeKey(K_LESSONS, JSON.stringify(lessons));
}

export async function loadProgress(): Promise<Progress> {
  const raw = readKey(K_PROGRESS);
  if (!raw) return { ...DEFAULT_PROGRESS };
  try {
    const obj = JSON.parse(raw);
    return { ...DEFAULT_PROGRESS, ...obj, last_positions: obj.last_positions ?? {} };
  } catch (e) {
    console.warn('[storage] failed to parse progress', e);
    return { ...DEFAULT_PROGRESS };
  }
}

export async function saveProgress(progress: Progress): Promise<void> {
  writeKey(K_PROGRESS, JSON.stringify(progress));
}

export async function loadUiLang(): Promise<LangPref> {
  const raw = readKey(K_LANG);
  return (raw as LangPref) || 'system';
}

export async function saveUiLang(pref: LangPref): Promise<void> {
  writeKey(K_LANG, pref);
}

// ---------------------------------------------------------------------------
// Export / import — the browser's answer to the desktop app's data folder.
// Clearing site data wipes localStorage, so a manual backup path matters more
// here than it does on Android.
// ---------------------------------------------------------------------------

export interface ExportBundle {
  app: 'Speak2Remember';
  version: 1;
  exported_at: string;
  lessons: Lesson[];
  progress: Progress;
}

export function buildExport(lessons: Lesson[], progress: Progress): ExportBundle {
  return {
    app: 'Speak2Remember',
    version: 1,
    exported_at: new Date().toISOString(),
    lessons,
    progress,
  };
}

/**
 * Parse an exported bundle. Also accepts a bare lessons array, which is what
 * the Python app's `lessons.json` contains.
 */
export function parseImport(text: string): { lessons: Lesson[]; progress: Progress | null } | null {
  let data: unknown;
  try {
    data = JSON.parse(text);
  } catch {
    return null;
  }

  if (Array.isArray(data)) {
    if (!looksLikeLessons(data)) return null;
    return { lessons: migrateContentId(data as Lesson[]).lessons, progress: null };
  }

  if (data && typeof data === 'object') {
    const bundle = data as Partial<ExportBundle>;
    if (Array.isArray(bundle.lessons) && looksLikeLessons(bundle.lessons)) {
      return {
        lessons: migrateContentId(bundle.lessons).lessons,
        progress: bundle.progress ? { ...DEFAULT_PROGRESS, ...bundle.progress } : null,
      };
    }
  }
  return null;
}

function looksLikeLessons(arr: unknown[]): boolean {
  // An empty export is legitimate; otherwise require the lesson shape.
  return arr.every(
    (l) => !!l && typeof l === 'object' && 'sid' in l && 'items' in l && Array.isArray((l as Lesson).items),
  );
}

/** Trigger a client-side download of the given bundle. */
export function downloadExport(bundle: ExportBundle): void {
  const blob = new Blob([JSON.stringify(bundle, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `speak2remember-${bundle.exported_at.slice(0, 10)}.json`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
